import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { EmptyState } from '@/components'
import { DEFAULT_LIMIT_PAGE, MERCAI_WAREHOUSES_OWNER_EMAIL } from '@/constants'
import {
    formatCurrency,
    formatDate,
    formatNumber,
    useParamsState,
} from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import {
    useInventoryList,
} from '@/services'
import { InventoryProps } from '@/types'
import {
    ArrowRightIcon,
    Button,
    Pagination,
    Select,
    TableColumns,
    TableColumnsColumnProps,
    Tag,
    Text,
    Tooltip,
} from '@mercai/clever'

import styles from './inventory-table.module.css'

import { InventoryTableProps } from './inventory-table.type'

export const InventoryTable = ({ uuid }: InventoryTableProps) => {
    const { t } = useTranslation('screenInventoryList')

    const [filter, setFilter] = useParamsState({
        page: '1',
        limit: `${DEFAULT_LIMIT_PAGE}`,
        search: '',
        status: '',
        ordering: 'created_at',
        catalogProductUuid: uuid,
    })

    useEffect(() => {
        if (filter.catalogProductUuid !== uuid) {
            setFilter({ ...filter, catalogProductUuid: uuid })
        }
    }, [uuid, filter])

    const { data, isLoading } = useInventoryList({
        params: {
            catalogProductUuid: uuid,
            limit: filter.limit,
            page: filter.page,
            search: filter.search,
            status: filter.status,
            ordering: filter.ordering,
        }
    })

    const [totalPages, setTotalPages] = useState(1)

    const handleFilter = (changedKey: string, newValue: string | number) => {
        if (changedKey === 'page') {
            setFilter({ ...filter, [changedKey]: `${newValue}` })
        } else {
            setFilter({ ...filter, [changedKey]: `${newValue}`, page: '1' })
        }
    }

    const columns: TableColumnsColumnProps<InventoryProps>[] = [
        {
            title: t('supplierColumn'),
            key: 'supplier',
            width: '18rem',
            render: ({ record }) => (
                <div className={styles['column-supplier']}>
                    {record?.supplier ? (
                        <Tooltip text={record?.supplier?.name || '-'} orientation="top">
                            <Link
                                target="_blank"
                                to={ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIERS.SHOW.fullPath(
                                    record?.supplier?.uuid || '',
                                )}
                                className={styles.link}
                            >
                                {record?.supplier?.name || '-'}
                            </Link>
                        </Tooltip>
                    ) : (
                        <Text>-</Text>
                    )}
                </div>
            ),
        },
        {
            title: t('warehouseColumn'),
            key: 'warehouse',
            width: '14rem',
            render: ({ record }) => (
                <div className={styles['column-supplier']}>
                    {record?.supplierWarehouse ? (
                        <Text>{record?.supplierWarehouse?.name || '-'}</Text>
                    ) : (
                        <Text>-</Text>
                    )}
                </div>
            ),
        },
        {
            title: t('deliveryOptionColumn'),
            key: 'delivery-option',
            width: '10rem',
            render: ({ record }) => (
                <div className={styles['column-supplier']}>
                    {record?.supplier ? (
                        record?.supplier?.email === MERCAI_WAREHOUSES_OWNER_EMAIL ? (
                            <Tag variant="success" model="light">
                                {t('deliveryOptionDeliveryOptionDeliverToOurWarehouse')}
                            </Tag>
                        ) : (
                            <Tag variant="info" model="light">
                                {t('deliveryOptionDeliveryOptionKeepAtSupplier')}
                            </Tag>
                        )
                    ) : (
                        <Text>-</Text>
                    )}
                </div>
            ),
        },
        {
            title: t('quantityColumn'),
            key: 'quantity',
            width: '12rem',
            render: ({ record }) => (
                <Text>{formatNumber(record.quantity || '0', undefined, 0)} KG</Text>
            ),
        },
        {
            title: t('costColumn'),
            key: 'cost',
            width: '12rem',
            render: ({ record }) => (
                <Text>
                    {record?.cost
                        ? formatCurrency(record.cost, 'es-MX', record.currency || 'USD')
                        : '0'}
                </Text>
            ),
        },
        {
            title: t('lastMovementColumn'),
            key: 'last-movement',
            width: '8rem',
            render: ({ record }) => (
                <Text>
                    {record?.lastInventoryTransaction?.updatedAt
                        ? formatDate(record?.lastInventoryTransaction?.updatedAt)
                        : '-'}
                </Text>
            ),
        },

        {
            title: '',
            key: 'actions',
            width: '5rem',
            render: ({ record }) => (
                <Button
                    href={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.INVENTORY.SHOW.fullPath(
                        record?.uuid || '',
                    )}
                    target="_blank"
                    model="round"
                >
                    <ArrowRightIcon />
                </Button>
            ),
        },
    ]

    useEffect(() => {
        if (data?.data?.count) {
            setTotalPages(
                Math.ceil(data?.data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
            )
        }
    }, [data])

    return (
        <div className={styles.core}>
            <TableColumns
                isLoading={isLoading}
                columns={columns}
                data={data?.data?.results || []}
            />

            {!isLoading && (!data?.data || data?.data?.results?.length === 0) && (
                <div className={styles['empty-state-container']}>
                    <EmptyState
                        title={t('tableEmptyTitle')}
                        description={t('tableEmptyDescription')}
                        buttonText={t('newProductButton')}
                        onClickButton={() =>
                            window.open(
                                ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.GENERATE_PURCHASE_ORDER.CREATE.fullPath(),
                                '_blank',
                            )
                        }
                    />
                </div>
            )}

            {data?.data?.results && data.data.results.length >= 1 && (
                <div className={styles.pagination}>
                    <div className={styles['pagination-show-registers']}>
                        <Text>
                            {t('tableShowRegisters', {
                                showNumber:
                                    (data?.data?.count || 0) < +filter.limit
                                        ? data?.data?.count
                                        : +filter.limit,
                                totalNumber: data?.data?.count || 0,
                            })}
                        </Text>

                        <Select
                            value={filter.limit}
                            onChange={(value) => handleFilter('limit', value)}
                            options={[
                                { label: '10', value: '10' },
                                { label: '20', value: '20' },
                                { label: '50', value: '50' },
                                { label: '100', value: '100' },
                            ]}
                        />
                    </div>

                    <Pagination
                        totalPages={totalPages}
                        onChange={(page) => handleFilter('page', page)}
                        currentPage={+filter.page as number}
                    />
                </div>
            )}
        </div>
    )
}
